/**
 * MediaSection.tsx - Media Gallery and Carousel Component
 *
 * This component displays fundraiser media (images and videos) in an interactive
 * carousel format with navigation controls, indicators, and auto-rotation support.
 *
 * Key Features:
 * - Image and video support with responsive display
 * - Auto-rotation with configurable timing
 * - Touch/swipe navigation for mobile devices
 * - Navigation arrows and dot indicators
 * - Drag operation detection to prevent conflicts
 * - Fallback media and error handling
 * - Empty state with helpful messaging
 * - YouTube video embedding support
 *
 * Props: None (uses MobX store for data)
 *
 * Dependencies: MobX store, fundraiser utilities, Lucide icons
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { getPrimaryFundraiserImage } from "../utils/fundraiserUtils";
import { CreateFundraiserModel, MediaModel } from "../models";
import { MediaType } from "../enums";

const HeroContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  /* Remove static transform that could interfere with drag operations */
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8fafc;
  touch-action: pan-x;
  will-change: auto;

  /* Allow override from parent component */
  .custom-media-section & {
    /* Height will be determined by hidden sizing element */
  }
`;

const MediaItem = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  transition: ${(props) =>
    props.isActive ? "opacity 0.5s ease-in-out" : "none"};
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: ${(props) => (props.isActive ? "opacity" : "auto")};
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  /* object-fit: fill; // creating issue for the images with larger height as the parent has fixed 600px height */
  object-fit: cover;
  object-position: center center;
  display: block;
  margin: 0;
  padding: 0;
`;

const MediaVideo = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const NavigationButton = styled.button<{ direction: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${(props) => props.direction}: 16px;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;

  ${MediaContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const MediaIndicators = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 0 8px;
  background: #ffffff;
`;

const Indicator = styled.button<{ isActive: boolean }>`
  width: ${(props) => (props.isActive ? "28px" : "12px")};
  height: 12px;
  border-radius: 6px;
  border: none;
  background: ${(props) => (props.isActive ? "#3b82f6" : "#cbd5e1")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.isActive ? "#2563eb" : "#94a3b8")};
  }
`;

const HeroContent = styled.div`
  padding: 16px 24px;
`;

const Title = styled.h3`
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 16px;
  line-height: 1.3;
  word-break: break-word;
`;

const Description = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #64748b;
  text-align: center;
  padding: 40px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 32px;
`;

const EmptyTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

// Default fallback media items
// const defaultMediaItems: FundRaiserLinkedMedia[] = [
//   {
//     mediaId: "default-1",
//     type: MediaType.Image,
//     src: "/0aa4bfc5-9ff7-49c3-8bcb-ac7e319686af.png",
//     title: "Clean Water Access",
//     order: 0,
//   },
// ];

type IMediaProps = {
  mediaItems: MediaModel[];
  isPreviewMode: boolean;
  fundraiser?: CreateFundraiserModel;
};

export const MediaSection: React.FC<IMediaProps> = observer(
  ({ mediaItems, fundraiser, isPreviewMode }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStartX, setTouchStartX] = useState(0);
    const [touchEndX, setTouchEndX] = useState(0);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [isStable, setIsStable] = useState(true);

    const selectedLayout = fundraiser?.selectedLayout || "progress-first";
    const rotateMedia = fundraiser?.rotateMedia || false;
    // Initialize component only once to prevent animation re-triggers
    React.useEffect(() => {
      if (!hasInitialized) {
        setHasInitialized(true);
      }
    }, [hasInitialized]);

    // Detect and prevent updates during drag operations
    React.useEffect(() => {
      let dragTimeout: NodeJS.Timeout;

      const observer = new MutationObserver((mutations) => {
        const hasDragOverlay = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof Element &&
              (node.classList?.contains("dnd-kit-drag-overlay") ||
                node.querySelector?.(".dnd-kit-drag-overlay"))
          )
        );

        if (hasDragOverlay) {
          setIsStable(false);
          clearTimeout(dragTimeout);
        } else {
          // Set stable after a short delay to ensure drag operation is complete
          dragTimeout = setTimeout(() => {
            setIsStable(true);
          }, 100);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => {
        observer.disconnect();
        clearTimeout(dragTimeout);
      };
    }, []);

    const primaryImageUrl = fundraiser
      ? getPrimaryFundraiserImage(fundraiser)?.fullURL
      : "";

    // Auto-rotation effect
    React.useEffect(() => {
      // Don't auto-rotate during drag operations, when component is unstable, or during any drag mode
      if (!isStable) return;

      // Check if any drag operation is active globally
      const isDragActive =
        document.querySelector(".dnd-kit-drag-overlay") !== null;
      if (isDragActive) return;

      if (rotateMedia && mediaItems.length > 1 && hasInitialized) {
        const interval = setInterval(() => {
          setCurrentIndex((prev) =>
            prev === mediaItems.length - 1 ? 0 : prev + 1
          );
        }, 6000);

        return () => clearInterval(interval);
      }
    }, [rotateMedia, mediaItems.length, hasInitialized, isStable]);

    const handleManualNavigation = (newIndex: number) => {
      setCurrentIndex(newIndex);
      // Stop auto-rotation when user manually navigates
      // Note: We don't update the store here to maintain the setting,
      // but we could add logic to temporarily pause rotation
    };

    const goToPrevious = () => {
      handleManualNavigation(
        currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1
      );
    };

    const goToNext = () => {
      handleManualNavigation(
        currentIndex === mediaItems.length - 1 ? 0 : currentIndex + 1
      );
    };

    const goToSlide = (index: number) => {
      handleManualNavigation(index);
    };

    // Touch handlers for mobile swipe
    const handleTouchStart = (e: React.TouchEvent) => {
      setTouchStartX(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      setTouchEndX(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!touchStartX || !touchEndX) return;

      const distance = touchStartX - touchEndX;
      const isLeftSwipe = distance > 50;
      const isRightSwipe = distance < -50;

      if (isLeftSwipe && mediaItems.length > 1) {
        goToNext();
      }
      if (isRightSwipe && mediaItems.length > 1) {
        goToPrevious();
      }
    };

    // If no media items at all, show empty state
    if (mediaItems.length === 0 && isPreviewMode) {
      return (
        <HeroContainer>
          <MediaContainer>
            <EmptyState>
              <EmptyIcon>📷</EmptyIcon>
              <EmptyTitle>No Media Added</EmptyTitle>
              <EmptyText>
                Add images or videos in the builder to showcase your fundraiser.
              </EmptyText>
            </EmptyState>
          </MediaContainer>
          <HeroContent>
            <Title>See the Impact in Action</Title>
            <Description>
              Add media content to show your supporters the impact of their
              donations and the work being done.
            </Description>
          </HeroContent>
        </HeroContainer>
      );
    }

    const currentMedia =
      mediaItems?.length > 0 ? mediaItems[currentIndex] : null;

    const getVideoEmbedUrl = (url: string) => {
      if (url.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    };

    const getDescription = (index: number, mediaItem: MediaModel) => {
      // Use the media item description if available, otherwise use title, otherwise generate fallback
      if (mediaItem?.description && mediaItem.description.trim() !== "") {
        return mediaItem.description;
      }
      return "";
    };

    if (!mediaItems || mediaItems.length === 0) {
      return null;
    }

    return (
      <HeroContainer
        style={{
          marginTop: selectedLayout === "Layout1" ? "32px" : "0",
        }}
      >
        <MediaContainer
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Hidden element to dictate MediaContainer's height based on current media's aspect ratio */}
          {currentMedia && currentMedia.type === MediaType.Image && (
            <img
              src={currentMedia.fullURL}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                visibility: "hidden",
                pointerEvents: "none",
                display: "block",
                margin: 0,
                padding: 0,
                position: "static",
              }}
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.src !== primaryImageUrl) {
                  img.src = primaryImageUrl;
                }
              }}
            />
          )}
          {currentMedia && currentMedia.type === MediaType.Video && (
            <div
              style={{
                width: "100%",
                paddingBottom: "56.25%" /* 16:9 aspect ratio for videos */,
                position: "relative",
                visibility: "hidden",
                pointerEvents: "none",
              }}
            >
              <iframe
                src={getVideoEmbedUrl(currentMedia.fullURL)}
                title="iframe video"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </div>
          )}

          {mediaItems.map((media, index) => (
            <MediaItem key={media.mediaId} isActive={index === currentIndex}>
              {media.type === MediaType.Image ? (
                <MediaImage
                  src={media.fullURL}
                  alt={media.title || `Media ${index + 1}`}
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    if (img.src !== primaryImageUrl) {
                      img.src = primaryImageUrl;
                    }
                  }}
                  className="media-img"
                />
              ) : (
                <MediaVideo
                  src={getVideoEmbedUrl(media.fullURL)}
                  title={media.title || `Video ${index + 1}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </MediaItem>
          ))}

          {mediaItems.length > 1 && (
            <>
              <NavigationButton direction="left" onClick={goToPrevious}>
                <ChevronLeft size={20} color="#64748b" />
              </NavigationButton>
              <NavigationButton direction="right" onClick={goToNext}>
                <ChevronRight size={20} color="#64748b" />
              </NavigationButton>
            </>
          )}
        </MediaContainer>

        {mediaItems.length > 1 && (
          <MediaIndicators>
            {mediaItems.map((_, index) => (
              <Indicator
                key={index}
                isActive={index === currentIndex}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </MediaIndicators>
        )}

        <HeroContent>
          {currentMedia?.title && <Title>{currentMedia.title}</Title>}
          {currentMedia && getDescription(currentIndex, currentMedia) && (
            <Description>
              {currentMedia && getDescription(currentIndex, currentMedia)}
            </Description>
          )}
        </HeroContent>
      </HeroContainer>
    );
  }
);
